<script setup lang="ts">
/**
 * HamburgerIcon — the three stacked bars inside a menu toggle, and the X they
 * become while the menu is open (#4085).
 *
 * `TopBar` and `PortalHeader` each declared this: the same three rules, byte for
 * byte apart from the class prefix, plus an open state that only the portal had.
 * #4080 gave the 44x44 outlined button *around* it an owner
 * (`assets/styles/header-action.css`) and deliberately stopped at the box —
 * this is its content, and `hamburgerIcon.spec.ts` is the ratchet that keeps a
 * third copy from being written by hand.
 *
 * It is an atom rather than a stylesheet — the arrangement `Checkbox` and
 * `Radio` have with `choice-control.css` (#3920) — because what the two callers
 * share is **markup and a state**, not only a box. Three `<span>`s cannot be
 * deduplicated by a stylesheet at all, and the open state would stay a class
 * every caller spells for itself, which is exactly how one of the two came to be
 * missing it.
 *
 * The bars are CSS rather than a lucide `Menu`/`X` pair because the shape
 * *morphs*: the outer two rotate through the middle while the middle one fades,
 * which two swapped icons cannot do. `AdminLayout`'s mobile header is that other
 * spelling, and it is deliberately outside this atom — folding it in is a
 * decision about what the admin header should look like, not a deduplication.
 *
 * **The button is the caller's.** Its box is `.header-action`, whether it is
 * shown at a given width is the caller's rule, and the `aria-label` /
 * `aria-expanded` that make it a control live there too. This draws the icon and
 * nothing else, which is why it is `aria-hidden`: a screen reader is told the
 * button's name and its expanded state, and three anonymous `<span>`s would add
 * nothing to either.
 */
interface Props {
  /**
   * Whether the menu the button controls is open. Both headers pass it, and that
   * is the #4085 decision rather than an accident: the button stays in the row
   * and stays operable when the menu opens, so the X is what says the next press
   * closes it again. A caller that leaves it out gets bars that never move —
   * honest only for a toggle that disappears while its menu is open.
   */
  open?: boolean
}

withDefaults(defineProps<Props>(), { open: false })
</script>

<template>
  <span class="hamburger" :class="{ 'hamburger--open': open }" aria-hidden="true">
    <span class="hamburger__line" />
    <span class="hamburger__line" />
    <span class="hamburger__line" />
  </span>
</template>

<style scoped>
/*
 * The bar's geometry, named so that the morph is *derived* from it instead of
 * restated: an outer bar travels exactly one bar plus one gap to reach the
 * middle, which is where the hard-coded `7px` in both copies came from. Change
 * the thickness or the gap here and the X still closes.
 *
 * These stay literals rather than becoming tokens on purpose. A hamburger's bar
 * is 2px because a hamburger's bar is 2px, not because the design system has a
 * 2px step — `--spacing-2xs` is the same number and answers a different question
 * — and one place holding them is what this atom is for.
 */
.hamburger {
  --hamburger-line-width: 20px;
  --hamburger-line-height: 2px;
  --hamburger-line-gap: 5px;
  --hamburger-line-travel: calc(var(--hamburger-line-height) + var(--hamburger-line-gap));

  display: flex;
  flex-direction: column;
  gap: var(--hamburger-line-gap);
}

.hamburger__line {
  display: block;
  width: var(--hamburger-line-width);
  height: var(--hamburger-line-height);
  /* `currentColor`, so the bars follow `.header-action`'s hover (#4080). */
  background: currentColor;
  border-radius: var(--radius-full);
  /*
   * Named properties rather than `all`, which is what both copies spelled: `all`
   * animates whatever a caller happens to change on the bars as well, and the
   * only two things that move here are the rotation and the middle bar's fade.
   */
  transition:
    transform var(--transition-duration-fast) var(--transition-easing-ease-out),
    opacity var(--transition-duration-fast) var(--transition-easing-ease-out);
}

.hamburger--open .hamburger__line:first-child {
  transform: translateY(var(--hamburger-line-travel)) rotate(45deg);
}

.hamburger--open .hamburger__line:nth-child(2) {
  opacity: 0;
}

.hamburger--open .hamburger__line:last-child {
  transform: translateY(calc(-1 * var(--hamburger-line-travel))) rotate(-45deg);
}
</style>
